import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { THING_KINDS, type ThingKind, type ThingToDo } from "@/lib/thingsToDo";

/** Repeatable rows for a things-to-do list (areas and collections). */
const ThingsToDoEditor = ({ value, onChange }: { value: ThingToDo[]; onChange: (next: ThingToDo[]) => void }) => {
  const update = (i: number, patch: Partial<ThingToDo>) => onChange(value.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const remove = (i: number) => onChange(value.filter((_, j) => j !== i));
  const add = () => onChange([...value, { name: "", kind: "other" }]);
  return (
    <div className="space-y-2">
      {value.length === 0 && (
        <p className="rounded-card border border-dashed px-3 py-3 text-[12px] text-muted-foreground">No places yet.</p>
      )}
      {value.map((t, i) => (
        <div key={i} className="rounded-card border bg-card/50 p-2 grid grid-cols-1 sm:grid-cols-[1fr_150px_90px_1fr_auto] gap-2 items-center">
          <Input value={t.name} onChange={(e) => update(i, { name: e.target.value })} placeholder="Playa Carmen surf break" className="rounded-input" aria-label="Place name" />
          <Select value={t.kind} onValueChange={(v) => update(i, { kind: v as ThingKind })}>
            <SelectTrigger className="rounded-input" aria-label="Kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {THING_KINDS.map((k) => (
                <SelectItem key={k.id} value={k.id}>
                  {k.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="number"
            min={0}
            value={t.minutesAway ?? ""}
            onChange={(e) => update(i, { minutesAway: e.target.value === "" ? undefined : Math.max(0, Math.round(Number(e.target.value))) })}
            placeholder="min"
            className="rounded-input"
            aria-label="Minutes away"
          />
          <Input value={t.url ?? ""} onChange={(e) => update(i, { url: e.target.value || undefined })} placeholder="https://… (optional)" className="rounded-input" aria-label="Link" />
          <Button type="button" variant="ghost" size="icon" className="rounded-full text-destructive" onClick={() => remove(i)} aria-label="Remove place">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" className="rounded-full gap-1.5" onClick={add}>
        <Plus className="h-3.5 w-3.5" /> Add place
      </Button>
    </div>
  );
};

export default ThingsToDoEditor;
